import React, { useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  Theme,
  Toolbar,
  Typography,
} from "@mui/material";
import { makeStyles, withStyles } from "@mui/styles";
import { FilterList as FilterIcon } from "@mui/icons-material";
import {
  IdsCheckBoxBlank as CheckBoxBlankIcon,
  IdsCheckBoxChecked as CheckBoxCheckedIcon,
  IdsExpandLess as IdsExpandLessIcon,
  IdsExpandMore as IdsExpandMoreIcon,
} from "components/shared/svgIcons";
import { TreeView } from "@mui/x-tree-view/TreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";

import UploadTargetsBtn from "components/shared/UploadTargetsBtn";
import LeftContent from "components/wrappers/LeftContent";
import useAuth from "hooks/useAuth";
import useCatalog from "hooks/useCatalog";
import useWidth from "hooks/useWidth";
import { colors } from "theme";
import { ITypeFilter, ITypeFilterOption } from "types/catalog";

const useStyles = makeStyles((theme: Theme) => ({
  treeItemContainer: { marginBottom: theme.spacing(4) },
  treeItemContent: {
    color: theme.palette.primary.main,
    backgroundColor: "transparent",
    marginBottom: theme.spacing(1.5),
    padding: 0,
    "&:hover": {
      color: theme.palette.primary.dark,
      backgroundColor: "transparent",
    },
  },
  treeItemGroup: { marginLeft: theme.spacing(1) },
  iconContainer: {
    order: 1,
    "& svg": { fontSize: "22px !important" },
  },
  filterLabel: {
    order: 0,
    width: "fit-content !important",
    color: "inherit",
    fontSize: `${theme.typography.button.fontSize} !important`,
    fontWeight: "600 !important",
    lineHeight: `${theme.typography.button.lineHeight} !important`,
    letterSpacing: theme.typography.button.letterSpacing,
    paddingLeft: "0 !important",
    paddingRight: theme.spacing(1.5),
    backgroundColor: "inherit !important",
  },
  filterOption: {
    "& + div": { marginTop: theme.spacing(2) },
    "& .MuiTypography-h6": { fontWeight: 400 },
    "& .MuiTypography-subtitle2": { fontWeight: 600, letterSpacing: 0.5 },
  },
  formControlLabel: {
    borderRadius: theme.shape.borderRadius,
    "&.Mui-disabled": {
      backgroundColor: theme.palette.grey[600],
      "& svg #border": {
        stroke: theme.palette.grey[300],
      },
    },
  },
  checkbox: {
    padding: theme.spacing(0, 1, 0, 0),
    marginLeft: 1,
    borderRadius: 0,
    "&:hover": { backgroundColor: "transparent" },
  },
  checkedIcon: {
    "& rect": {
      fill: theme.palette.secondary.main,
    },
  },
  sidebarDrawer: { width: 328 },
  subtitleH: {
    fontSize: theme.typography.h4.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

// we should make a shared styled component for this
const ClearFiltersButton = withStyles((theme: Theme) => ({
  root: {
    color: theme.palette.secondary.main,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body1.fontSize,
    fontWeight: 600,
    minWidth: "fit-content",
    padding: 0,
    "&:hover:not(:focus)": {
      textDecoration: "underline",
      textUnderlineOffset: "4px",
    },
    "&:focus-visible": {
      outline: `2px solid ${colors.nero[300]}`,
      outlineOffset: 2,
      borderRadius: 1,
    },
    "&:focus": { borderRadius: 1 },
  },
}))(ButtonBase);

const UploadTargetButton = ({ fullWidth = false }: { fullWidth?: boolean }) => {
  const { isLoggedIn } = useAuth();
  if (!isLoggedIn) return null;

  return (
    <UploadTargetsBtn
      size={fullWidth ? "medium" : "small"}
      fullWidth={fullWidth}
    />
  );
};

const FilterPanel = () => {
  const { isLoggedIn } = useAuth();
  const classes = useStyles();
  const width = useWidth();
  const [visibleSidebar, setVisibleSidebar] = useState<boolean>(false);
  const { filters, resetFilters, setFilters } = useCatalog();

  const changeFilter = (filterType: string, filterOptionId: number): void => {
    setFilters(
      filters.map((filter: ITypeFilter) => {
        if (filter.type === filterType) {
          return {
            ...filter,
            options: filter.options.map((filterOption: ITypeFilterOption) =>
              filterOption.id === filterOptionId
                ? { ...filterOption, selected: !Boolean(filterOption.selected) }
                : filterOption
            ),
          };
        }
        return filter;
      })
    );
  };
  const FilterContent = React.memo(() => (
    <>
      <Grid container justifyContent="space-between" alignItems="baseline">
        <Typography
          variant="subtitle1"
          component="h4"
          className={classes.subtitleH}
        >
          Filter by
        </Typography>
        <ClearFiltersButton onClick={resetFilters}>
          Clear All
        </ClearFiltersButton>
      </Grid>
      <Box mt={1} mb={2.5}>
        <Divider />
      </Box>
      <TreeView
        expanded={filters
          .filter((filter: ITypeFilter) => filter.expanded)
          .map((filter) => filter.type)}
        defaultCollapseIcon={<IdsExpandMoreIcon color="inherit" />}
        defaultExpandIcon={<IdsExpandLessIcon color="inherit" />}
      >
        {filters.map((filter: ITypeFilter) => (
          <TreeItem
            key={filter.type}
            classes={{
              label: classes.filterLabel,
              root: classes.treeItemContainer,
              content: classes.treeItemContent,
              iconContainer: classes.iconContainer,
              group: classes.treeItemGroup,
            }}
            onClick={() =>
              setFilters(
                filters.map((item) => {
                  if (item.type === filter.type) {
                    return {
                      ...item,
                      expanded: !item.expanded,
                    };
                  }
                  return item;
                })
              )
            }
            nodeId={filter.type}
            label={filter.label}
          >
            <Grid container>
              {filter.options.map((filterOption: ITypeFilterOption) => (
                <Grid
                  key={filterOption.id}
                  item
                  xs={12}
                  className={classes.filterOption}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(filterOption.selected)}
                        onChange={() =>
                          changeFilter(filter.type, filterOption.id)
                        }
                        name={filterOption.type}
                        color="secondary"
                        className={classes.checkbox}
                        icon={<CheckBoxBlankIcon />}
                        checkedIcon={
                          <CheckBoxCheckedIcon
                            className={classes.checkedIcon}
                          />
                        }
                        disableRipple
                      />
                    }
                    disabled={!!filterOption.disabled}
                    label={
                      typeof filterOption.label === "string" ? (
                        <Typography component="span" variant="body2">
                          {filterOption.label}
                        </Typography>
                      ) : (
                        filterOption.label
                      )
                    }
                    classes={{
                      root: classes.formControlLabel,
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </TreeItem>
        ))}
      </TreeView>
    </>
  ));

  if (["xs", "sm"].includes(width)) {
    return (
      <LeftContent>
        <Toolbar variant="dense" disableGutters>
          <UploadTargetButton />
          <Box ml={isLoggedIn ? 1 : 0}>
            <Button
              color="primary"
              variant="outlined"
              startIcon={<FilterIcon />}
              size="small"
              onClick={() => setVisibleSidebar(true)}
            >
              Filter
            </Button>
          </Box>
        </Toolbar>
        <Drawer
          open={visibleSidebar}
          PaperProps={{ className: classes.sidebarDrawer }}
          onClose={() => setVisibleSidebar(false)}
        >
          <Box paddingX={3} paddingY={2}>
            <FilterContent />
          </Box>
        </Drawer>
      </LeftContent>
    );
  }

  return (
    <LeftContent>
      {isLoggedIn && (
        <>
          <Typography variant="body1">
            Interested in creating your own custom panel? Click below to get
            started.
          </Typography>
          <Box mt={2}>
            <UploadTargetButton fullWidth />
          </Box>
        </>
      )}
      <Box my={isLoggedIn ? 4 : 0}>
        <FilterContent />
      </Box>
    </LeftContent>
  );
};

export default FilterPanel;
